import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCompetitions } from "../../../context/CompetitionsContext";
import { PaymentStatus } from "../../../types/payments.types";
import { PaymentsAPI } from "../../../APIs/payments.api";

const StatusContent = {
  [PaymentStatus.APPROVED]: {
    icon: (
      <svg
        className="w-12 h-12 text-green-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    ),
    title: "¡Pago realizado con éxito!",
    message:
      "Gracias por completar tu inscripción. Recibimos tu pago correctamente.",
    buttonColor: "bg-green-500 hover:bg-green-600",
    progressColor: "bg-green-500",
  },
  [PaymentStatus.AUTHORIZED]: {
    icon: (
      <svg
        className="w-12 h-12 text-blue-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: "Pago Autorizado",
    message: "Tu pago ha sido autorizado y está siendo procesado.",
    buttonColor: "bg-blue-500 hover:bg-blue-600",
    progressColor: "bg-blue-500",
  },
  [PaymentStatus.IN_PROCESS]: {
    icon: (
      <svg
        className="w-12 h-12 text-yellow-500 animate-spin"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    ),
    title: "Procesando Pago",
    message: "Estamos procesando tu pago. Por favor, espera un momento.",
    buttonColor: "bg-yellow-500 hover:bg-yellow-600",
    progressColor: "bg-yellow-500",
  },
  [PaymentStatus.PENDING]: {
    icon: (
      <svg
        className="w-12 h-12 text-yellow-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: "Pago Pendiente",
    message:
      "Tu pago está pendiente de confirmación. Te notificaremos cuando se complete.",
    buttonColor: "bg-yellow-500 hover:bg-yellow-600",
    progressColor: "bg-yellow-500",
  },
  [PaymentStatus.CANCELED]: {
    icon: (
      <svg
        className="w-12 h-12 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    ),
    title: "Pago Cancelado",
    message: "El pago ha sido cancelado. Puedes intentar nuevamente.",
    buttonColor: "bg-gray-500 hover:bg-gray-600",
    progressColor: "bg-gray-500",
  },
  [PaymentStatus.CHARGED_BACK]: {
    icon: (
      <svg
        className="w-12 h-12 text-red-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: "Pago Disputado",
    message:
      "El pago está en disputa. Nuestro equipo de soporte se pondrá en contacto contigo.",
    buttonColor: "bg-red-500 hover:bg-red-600",
    progressColor: "bg-red-500",
  },
  [PaymentStatus.IN_MEDIATION]: {
    icon: (
      <svg
        className="w-12 h-12 text-orange-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
        />
      </svg>
    ),
    title: "Pago en Mediación",
    message: "El pago está en proceso de mediación. Te mantendremos informado.",
    buttonColor: "bg-orange-500 hover:bg-orange-600",
    progressColor: "bg-orange-500",
  },
  [PaymentStatus.REFUNDED]: {
    icon: (
      <svg
        className="w-12 h-12 text-purple-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
        />
      </svg>
    ),
    title: "Pago Reembolsado",
    message: "El pago ha sido reembolsado a tu cuenta.",
    buttonColor: "bg-purple-500 hover:bg-purple-600",
    progressColor: "bg-purple-500",
  },
  [PaymentStatus.REJECTED]: {
    icon: (
      <svg
        className="w-12 h-12 text-red-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: "Pago Rechazado",
    message:
      "Lo sentimos, el pago fue rechazado. Por favor, intenta con otro método de pago.",
    buttonColor: "bg-red-500 hover:bg-red-600",
    progressColor: "bg-red-500",
  },
};

export function SuccessPayment() {
  const navigate = useNavigate();
  const { getCompetitions } = useCompetitions();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.PENDING
  );
  const [countdown, setCountdown] = useState(5);
  const [error, setError] = useState<string | null>(null);

  const paymentId = searchParams.get("payment_id");

  const redirectToHome = useCallback(() => {
    getCompetitions();
    navigate("/dashboard/competitions", { replace: true });
  }, [navigate, getCompetitions]);

  useEffect(() => {
    if (countdown === 0) {
      if (
        paymentStatus === PaymentStatus.APPROVED ||
        paymentStatus === PaymentStatus.AUTHORIZED
      ) {
        redirectToHome();
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, redirectToHome, paymentStatus]);

  useEffect(() => {
    if (!paymentId) {
      setError("No se encontró ID de pago");
      return;
    }

    const fetchPaymentStatus = async () => {
      try {
        const data = await PaymentsAPI.getPaymentStatus(
          Number.parseInt(paymentId)
        );
        if (data.statusCode === 200) {
          setPaymentStatus(data.data);
        } else {
          setError("Error al verificar el estado del pago");
        }
      } catch (err) {
        setError("Error al conectar con el servidor");
      }
    };

    fetchPaymentStatus();
  }, [paymentId]);

  if (error) {
    return (
      <div className="flex items-center justify-center mt-8">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-sm">
          <div className="flex justify-center">
            <svg
              className="w-12 h-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="mt-4 text-2xl font-semibold text-gray-800">Error</h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <button
            onClick={redirectToHome}
            className="mt-6 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-300"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const content = StatusContent[paymentStatus];

  return (
    <div className="flex items-center justify-center mt-8">
      <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-sm">
        <div className="flex justify-center">{content.icon}</div>
        <h2 className="mt-4 text-2xl font-semibold text-gray-800">
          {content.title}
        </h2>
        <p className="mt-2 text-gray-600">{content.message}</p>
        {(paymentStatus === PaymentStatus.APPROVED ||
          paymentStatus === PaymentStatus.AUTHORIZED) && (
          <div className="mt-6">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`${content.progressColor} h-2.5 rounded-full`}
                style={{ width: `${(countdown / 5) * 100}%` }}
              ></div>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Redirigiendo en {countdown} segundos...
            </p>
          </div>
        )}
        <button
          onClick={redirectToHome}
          className={`mt-6 px-4 py-2 ${content.buttonColor} text-white rounded-lg transition duration-300`}
        >
          {paymentStatus === PaymentStatus.APPROVED ||
          paymentStatus === PaymentStatus.AUTHORIZED
            ? "Redirigir ahora"
            : "Volver al inicio"}
        </button>
      </div>
    </div>
  );
}
